import { useContext } from 'react';

import { AppContext } from './app-provider';
import type { AppState } from './types';

export const useAppContext = () => {
	const context = useContext(AppContext);

	if (!context) {
		throw new Error('useAppContext must be used within an AppProvider');
	}

	const { state, setState } = context;

	const setProject = (project: AppState['project']) => {
		setState((prevState) => ({ ...prevState, project }));
	};

	const setSearchProjectValue = (value: AppState['searchProjectValue']) => {
		setState((prevState) => ({ ...prevState, searchProjectValue: value }));
	};

	const setSearchStatusId = (value: AppState['searchStatusId']) => {
		setState((prevState) => ({ ...prevState, searchStatusId: value }));
	};

	const setSelectedDocument = (value: AppState['selectedDocument']) => {
		setState((prevState) => ({ ...prevState, selectedDocument: value }));
	};

	const setToolsForSelect = (toolsForSelect: AppState['toolsForSelect']) => {
		setState((prevState) => ({ ...prevState, toolsForSelect }));
	};

	const showToast = ({ message, variant }: { message: AppState['toast']['message']; variant?: AppState['toast']['variant'] }) => {
		setState((prevState) => ({
			...prevState,
			toast: {
				message,
				variant,
				visible: true,
			},
		}));

		setTimeout(() => {
			setState((prevState) => ({
				...prevState,
				toast: {
					...prevState.toast,
					visible: false,
				},
			}));
		}, 5000);
	};

	const setIsToastVisible = (visible: AppState['toast']['visible']) => {
		setState((prevState) => ({ ...prevState, toast: { ...prevState.toast, visible } }));
	};

	const setIsLoading = (isLoading: AppState['isLoading']) => {
		setState((prevState) => ({ ...prevState, isLoading }));
	};

	const setAppConfig = (data: AppState['appConfig']) => {
		setState((prevState) => ({ ...prevState, appConfig: data }));
	};

	const setDocumentTypesURLS = (documentTypeURLS: AppState['documentTypeURLS']) => {
		setState((prevState) => ({ ...prevState, documentTypeURLS }));
	};

	const setCustomers = (customers: AppState['customers']) => {
		setState((prevState) => ({ ...prevState, customers }));
	};

	const setReleaseNotesStatus = (releaseNotesStatus: AppState['releaseNotesStatus']) => {
		setState((prevState) => ({ ...prevState, releaseNotesStatus }));
	};

	const setProjectContentTabIndex = (projectContentTabIndex: AppState['projectContentTabIndex']) => {
		setState((prevState) => ({ ...prevState, projectContentTabIndex }));
	};

	const setUnselectedProjectMessage = (unselectedProjectMessage: AppState['unselectedProjectMessage']) => {
		setState((prevState) => ({ ...prevState, unselectedProjectMessage }));
	};

	const setIsUnacceptedAccesses = (isUnacceptedAccesses: AppState['isUnacceptedAccesses']) => {
		setState((prevState) => ({ ...prevState, isUnacceptedAccesses }));
	};

	const setDocumentTypes = (documentTypes: AppState['documentTypes']) => {
		setState((prevState) => ({ ...prevState, documentTypes }));
	};

	return {
		...state,
		setProject,
		setSearchProjectValue,
		setSearchStatusId,
		setSelectedDocument,
		setToolsForSelect,
		showToast,
		setIsToastVisible,
		setIsLoading,
		setAppConfig,
		setDocumentTypesURLS,
		setCustomers,
		setReleaseNotesStatus,
		setProjectContentTabIndex,
		setUnselectedProjectMessage,
		setIsUnacceptedAccesses,
		setDocumentTypes,
	};
};
